import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Grid,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Avatar,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Collapse,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import {
    Shield,
    Search,
    ChevronDown,
    ChevronRight,
    CheckCircle,
    PlusCircle,
    MinusCircle,
    ArrowRight,
    ArrowLeft,
    X as CloseIcon,
    Building,
    User,
    AlertCircle,
    Info,
} from 'lucide-react';
import { showSnackbar } from '../../components/SnackBar/SnackBar';
import { permissionsApi, getLandlordId, propertiesApi } from '../../services/api';

const themeColor = '#2C5283';

// ==================== STYLED COMPONENTS ====================

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    border: '1px solid #DDE6ED',
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
}));

const TransferContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    minHeight: '600px',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
    },
}));

const PermissionList = styled(Paper)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(1.5),
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    maxHeight: '70vh',
    overflow: 'auto',
    minWidth: '350px',
    [theme.breakpoints.down('md')]: {
        minWidth: 'unset',
        maxHeight: '400px',
    },
}));

const PermissionItem = styled(Box)(({ theme, sourceType }) => ({
    padding: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    border: '1px solid',
    borderLeftWidth: '3px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderColor: sourceType === 'ROLE' ? '#1890ff' :
                sourceType === 'GRANT' ? '#52c41a' :
                sourceType === 'REVOKE' ? '#ff4d4f' : '#d9d9d9',
    backgroundColor: sourceType === 'ROLE' ? '#e6f7ff' :
                    sourceType === 'GRANT' ? '#f6ffed' :
                    sourceType === 'REVOKE' ? '#fff1f0' : '#fafafa',
    '&:hover': {
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        transform: 'translateY(-1px)',
    },
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.75),
    backgroundColor: '#f8f9fa',
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
        backgroundColor: '#e9ecef',
    },
}));

const StatsCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
    textAlign: 'center',
    border: '1px solid',
    borderRadius: theme.spacing(0.75),
}));

// ==================== MAIN COMPONENT ====================

const PermissionManagement = () => {
    const { subUserId } = useParams();
    const navigate = useNavigate();
    const landlordId = getLandlordId();

    // ==================== STATE ====================

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchAvailable, setSearchAvailable] = useState('');
    const [searchUser, setSearchUser] = useState('');
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [notes, setNotes] = useState('');

    // Data state
    const [allPermissions, setAllPermissions] = useState([]);
    const [effectivePermissions, setEffectivePermissions] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [properties, setProperties] = useState([]);
    const [userData, setUserData] = useState(null);
    const [categories, setCategories] = useState([]);

    // UI state
    const [expandedCategories, setExpandedCategories] = useState({});
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
        type: 'warning',
    });

    // ==================== EFFECTS ====================

    useEffect(() => {
        if (landlordId && subUserId) {
            loadAllData();
        }
    }, [landlordId, subUserId, selectedProperty]);

    // ==================== DATA LOADING ====================

    const loadAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadProperties(),
                loadAllPermissions(),
                loadEffectivePermissions(),
                loadOverrides(),
            ]);
        } catch (error) {
            console.error('Error loading data:', error);
            showSnackbar({
                title: 'Error!',
                message: 'Failed to load permission data. Please refresh the page.',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' },
            });
        } finally {
            setLoading(false);
        }
    };

    const loadProperties = async () => {
        try {
            const response = await propertiesApi.getAll(landlordId);
            if (response.ok) {
                setProperties(response.data || []);
            }
        } catch (error) {
            console.error('Error loading properties:', error);
        }
    };

    const loadAllPermissions = async () => {
        try {
            const response = await permissionsApi.getAll({ landlord_id: landlordId });
            if (response.ok) {
                setAllPermissions(response.data.permissions || []);
                setCategories(response.data.categories || []);

                // Initialize all categories as expanded
                const expanded = {};
                (response.data.categories || []).forEach(cat => {
                    expanded[cat] = true;
                });
                setExpandedCategories(expanded);
            }
        } catch (error) {
            console.error('Error loading all permissions:', error);
        }
    };

    const loadEffectivePermissions = async () => {
        try {
            const data = {
                landlord_id: landlordId,
                sub_user_id: parseInt(subUserId),
            };

            if (selectedProperty) {
                data.property_id = selectedProperty;
            }

            const response = await permissionsApi.getEffective(data);
            if (response.ok) {
                setEffectivePermissions(response.data.permissions || []);
                setUserData({
                    name: response.data.user_name,
                    email: response.data.email,
                    totalPermissions: response.data.total_permissions,
                });
            }
        } catch (error) {
            console.error('Error loading effective permissions:', error);
        }
    };

    const loadOverrides = async () => {
        try {
            const data = {
                landlord_id: landlordId,
                sub_user_id: parseInt(subUserId),
            };

            if (selectedProperty) {
                data.property_id = selectedProperty;
            }

            const response = await permissionsApi.getOverrides(data);
            if (response.ok) {
                setOverrides(response.data.overrides || []);
            }
        } catch (error) {
            console.error('Error loading overrides:', error);
        }
    };

    // ==================== HELPER FUNCTIONS ====================

    const getAvailablePermissions = () => {
        const userPermissionNames = new Set(
            effectivePermissions.map(p => p.permission_name)
        );

        const revokedPermissionNames = new Set(
            overrides
                .filter(o => o.grant_type === 'REVOKE')
                .map(o => o.permission_name)
        );

        return allPermissions.filter(p =>
            !userPermissionNames.has(p.permission_name) &&
            !revokedPermissionNames.has(p.permission_name)
        );
    };

    const groupPermissionsByCategory = (permissions) => {
        const grouped = {};
        permissions.forEach(perm => {
            if (!grouped[perm.permission_category]) {
                grouped[perm.permission_category] = [];
            }
            grouped[perm.permission_category].push(perm);
        });
        return grouped;
    };

    const filterPermissions = (permissions, searchTerm) => {
        if (!searchTerm) return permissions;
        const term = searchTerm.toLowerCase();
        return permissions.filter(p =>
            p.permission_name.toLowerCase().includes(term) ||
            p.permission_description.toLowerCase().includes(term)
        );
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    // ==================== PERMISSION ACTIONS ====================

    const handleGrantPermission = async (permissionName) => {
        setSaving(true);
        try {
            const response = await permissionsApi.grant({
                landlord_id: landlordId,
                sub_user_id: parseInt(subUserId),
                permission_name: permissionName,
                property_id: selectedProperty,
                notes: notes || undefined,
            });

            if (response.ok || response.data?.Success) {
                showSnackbar({
                    title: 'Success!',
                    message: `Permission "${permissionName}" granted successfully!`,
                    type: 'success',
                    position: { vertical: 'bottom', horizontal: 'right' },
                });

                setNotes('');
                await loadEffectivePermissions();
                await loadOverrides();
            } else {
                throw new Error(response.data?.Message || 'Failed to grant permission');
            }
        } catch (error) {
            console.error('Error granting permission:', error);
            showSnackbar({
                title: 'Error!',
                message: error.message || 'Failed to grant permission. Please try again.',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' },
            });
        } finally {
            setSaving(false);
        }
    };

    const handleRevokePermission = async (permissionName, isRolePermission) => {
        if (isRolePermission) {
            setConfirmDialog({
                open: true,
                title: 'Revoke Role Permission',
                message: `This will remove "${permissionName}" from the user's role. The user will no longer have this permission. Are you sure?`,
                type: 'warning',
                onConfirm: () => performRevoke(permissionName),
            });
        } else {
            await performRevoke(permissionName);
        }
    };

    const performRevoke = async (permissionName) => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setSaving(true);

        try {
            const response = await permissionsApi.revoke({
                landlord_id: landlordId,
                sub_user_id: parseInt(subUserId),
                permission_name: permissionName,
                property_id: selectedProperty,
                notes: notes || `Revoked on ${new Date().toLocaleDateString()}`,
            });

            if (response.ok || response.data?.Success) {
                showSnackbar({
                    title: 'Success!',
                    message: `Permission "${permissionName}" revoked successfully!`,
                    type: 'success',
                    position: { vertical: 'bottom', horizontal: 'right' },
                });

                setNotes('');
                await loadEffectivePermissions();
                await loadOverrides();
            } else {
                throw new Error(response.data?.Message || 'Failed to revoke permission');
            }
        } catch (error) {
            console.error('Error revoking permission:', error);
            showSnackbar({
                title: 'Error!',
                message: error.message || 'Failed to revoke permission. Please try again.',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' },
            });
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveOverride = async (permissionName, overrideType) => {
        const action = overrideType === 'GRANT' ? 'remove this grant' : 'remove this revoke';

        setConfirmDialog({
            open: true,
            title: 'Remove Override',
            message: `This will ${action} and revert "${permissionName}" to the role default. Continue?`,
            type: 'info',
            onConfirm: () => performRemoveOverride(permissionName),
        });
    };

    const performRemoveOverride = async (permissionName) => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setSaving(true);

        try {
            const response = await permissionsApi.removeOverride({
                landlord_id: landlordId,
                sub_user_id: parseInt(subUserId),
                permission_name: permissionName,
                property_id: selectedProperty,
            });

            if (response.status === 404 || response.data?.Error) {
                throw new Error(response.data?.Message || 'No override found for this permission');
            }

            if (response.ok || response.data?.Success) {
                showSnackbar({
                    title: 'Success!',
                    message: 'Override removed. Permission reverted to role default.',
                    type: 'success',
                    position: { vertical: 'bottom', horizontal: 'right' },
                });

                await loadEffectivePermissions();
                await loadOverrides();
            } else {
                throw new Error(response.data?.Message || 'Failed to remove override');
            }
        } catch (error) {
            console.error('Error removing override:', error);
            showSnackbar({
                title: 'Error!',
                message: error.message || 'Failed to remove override. Please try again.',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' },
            });
        } finally {
            setSaving(false);
        }
    };

    // ==================== RENDER HELPERS ====================

    const renderPermissionBadge = (permission, inUserList = false) => {
        const override = overrides.find(o => o.permission_name === permission.permission_name);
        const sourceType = permission.source_type || (override ? override.grant_type : null);

        return (
            <PermissionItem key={permission.permission_name} sourceType={sourceType}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sourceType === 'ROLE' && <CheckCircle size={14} color="#1890ff" />}
                            {sourceType === 'GRANT' && <PlusCircle size={14} color="#52c41a" />}
                            {sourceType === 'REVOKE' && <MinusCircle size={14} color="#ff4d4f" />}

                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    textDecoration: sourceType === 'REVOKE' ? 'line-through' : 'none',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {permission.permission_description}
                            </Typography>

                            {sourceType === 'ROLE' && permission.role_name && (
                                <Chip
                                    label={permission.role_name}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#e6f7ff',
                                        color: '#1890ff',
                                        height: '18px',
                                        fontSize: '0.65rem',
                                        '& .MuiChip-label': { px: 0.75 }
                                    }}
                                />
                            )}

                            {sourceType === 'GRANT' && (
                                <Chip
                                    label="+"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#f6ffed',
                                        color: '#52c41a',
                                        height: '18px',
                                        fontSize: '0.65rem',
                                        minWidth: '24px',
                                        '& .MuiChip-label': { px: 0.5 }
                                    }}
                                />
                            )}

                            {sourceType === 'REVOKE' && (
                                <Chip
                                    label="-"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#fff1f0',
                                        color: '#ff4d4f',
                                        height: '18px',
                                        fontSize: '0.65rem',
                                        minWidth: '24px',
                                        '& .MuiChip-label': { px: 0.5 }
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {inUserList ? (
                            sourceType === 'ROLE' ? (
                                <Tooltip title="Revoke">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRevokePermission(permission.permission_name, true)}
                                        disabled={saving}
                                        sx={{ color: '#ff4d4f', p: 0.5 }}
                                    >
                                        <MinusCircle size={16} />
                                    </IconButton>
                                </Tooltip>
                            ) : sourceType === 'GRANT' ? (
                                <Tooltip title="Remove">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveOverride(permission.permission_name, 'GRANT')}
                                        disabled={saving}
                                        sx={{ p: 0.5 }}
                                    >
                                        <CloseIcon size={16} />
                                    </IconButton>
                                </Tooltip>
                            ) : null
                        ) : (
                            <Tooltip title="Grant">
                                <IconButton
                                    size="small"
                                    onClick={() => handleGrantPermission(permission.permission_name)}
                                    disabled={saving}
                                    sx={{ color: '#52c41a', p: 0.5 }}
                                >
                                    <PlusCircle size={16} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </PermissionItem>
        );
    };

    const renderPermissionsByCategory = (permissions, inUserList = false) => {
        const grouped = groupPermissionsByCategory(permissions);

        return Object.entries(grouped).map(([category, perms]) => (
            <Box key={category} sx={{ mb: 1 }}>
                <CategoryHeader onClick={() => toggleCategory(category)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {expandedCategories[category] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {category.replace(/_/g, ' ')}
                        </Typography>
                        <Chip
                            label={perms.length}
                            size="small"
                            sx={{
                                height: '18px',
                                fontSize: '0.7rem',
                                '& .MuiChip-label': { px: 0.75 }
                            }}
                        />
                    </Box>
                </CategoryHeader>

                <Collapse in={expandedCategories[category]}>
                    <Box sx={{ pl: 1, pt: 0.5 }}>
                        {perms.map(perm => renderPermissionBadge(perm, inUserList))}
                    </Box>
                </Collapse>
            </Box>
        ));
    };

    // Continue to render in next part...

    const availablePermissions = filterPermissions(getAvailablePermissions(), searchAvailable);
    const userPermissions = filterPermissions(effectivePermissions, searchUser);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} sx={{ color: themeColor }} />
                    <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                        Loading permission management...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', p: 2 }}>
            <Box sx={{ maxWidth: '1800px', margin: '0 auto' }}>
                {/* Compact Header */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowLeft size={16} />}
                            onClick={() => navigate(-1)}
                            size="small"
                        >
                            Back
                        </Button>

                        {/* Inline Statistics */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Chip
                                icon={<CheckCircle size={16} />}
                                label={`${effectivePermissions.length} Total`}
                                sx={{ backgroundColor: '#e6f7ff', color: '#1890ff', fontWeight: 600 }}
                            />
                            <Chip
                                icon={<PlusCircle size={16} />}
                                label={`${overrides.filter(o => o.grant_type === 'GRANT').length} Granted`}
                                sx={{ backgroundColor: '#f6ffed', color: '#52c41a', fontWeight: 600 }}
                            />
                            <Chip
                                icon={<MinusCircle size={16} />}
                                label={`${overrides.filter(o => o.grant_type === 'REVOKE').length} Revoked`}
                                sx={{ backgroundColor: '#fff1f0', color: '#ff4d4f', fontWeight: 600 }}
                            />
                        </Box>
                    </Box>

                    <StyledCard>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ width: 40, height: 40, bgcolor: themeColor }}>
                                    <User size={20} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: themeColor, lineHeight: 1.2 }}>
                                        Manage Permissions
                                        {userData && (
                                            <Typography component="span" sx={{ ml: 1, color: '#666', fontSize: '0.9rem', fontWeight: 400 }}>
                                                - {userData.name}
                                            </Typography>
                                        )}
                                    </Typography>
                                    {userData && (
                                        <Typography variant="caption" color="textSecondary">
                                            {userData.email}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Inline Filters */}
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                        <InputLabel>Property Filter</InputLabel>
                                        <Select
                                            value={selectedProperty || ''}
                                            label="Property Filter"
                                            onChange={(e) => setSelectedProperty(e.target.value || null)}
                                        >
                                            <MenuItem value="">
                                                <em>All Properties (Global)</em>
                                            </MenuItem>
                                            {properties.map((property) => (
                                                <MenuItem key={property.property_id} value={property.property_id}>
                                                    {property.property_name} - {property.location}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        size="small"
                                        label="Notes (optional)"
                                        placeholder="Add notes..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        sx={{ minWidth: 200 }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </StyledCard>
                </Box>

                {/* Main Transfer Interface */}
                <StyledCard>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <TransferContainer>
                            {/* Available Permissions (Left Side) */}
                            <PermissionList elevation={1}>
                                <Box sx={{ mb: 1, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                            Available Permissions
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {availablePermissions.length} available
                                        </Typography>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search..."
                                        value={searchAvailable}
                                        onChange={(e) => setSearchAvailable(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search size={16} color="#666" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                            }
                                        }}
                                    />
                                </Box>

                                {availablePermissions.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <CheckCircle size={36} color="#ccc" />
                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                            {searchAvailable
                                                ? 'No matches found'
                                                : 'All permissions assigned'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    renderPermissionsByCategory(availablePermissions, false)
                                )}
                            </PermissionList>

                            {/* Center Arrow Indicators */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    alignItems: 'center',
                                    mx: 1,
                                }}
                            >
                                <Tooltip title="Grant Permission">
                                    <Box sx={{ color: '#52c41a' }}>
                                        <ArrowRight size={24} />
                                    </Box>
                                </Tooltip>
                                <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
                                <Tooltip title="Revoke Permission">
                                    <Box sx={{ color: '#ff4d4f' }}>
                                        <ArrowLeft size={24} />
                                    </Box>
                                </Tooltip>
                            </Box>

                            {/* User's Permissions (Right Side) */}
                            <PermissionList elevation={1}>
                                <Box sx={{ mb: 1, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                            User's Permissions
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {userPermissions.length} assigned
                                        </Typography>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search..."
                                        value={searchUser}
                                        onChange={(e) => setSearchUser(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search size={16} color="#666" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                            }
                                        }}
                                    />
                                </Box>

                                {userPermissions.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <AlertCircle size={36} color="#ccc" />
                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                            {searchUser
                                                ? 'No matches found'
                                                : 'No permissions assigned'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    renderPermissionsByCategory(userPermissions, true)
                                )}
                            </PermissionList>
                        </TransferContainer>
                    </CardContent>
                </StyledCard>

                {/* Confirmation Dialog */}
                <Dialog
                    open={confirmDialog.open}
                    onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {confirmDialog.type === 'warning' && <AlertCircle size={24} color="#ff9800" />}
                        {confirmDialog.type === 'info' && <Info size={24} color="#1890ff" />}
                        {confirmDialog.title}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>{confirmDialog.message}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            onClick={confirmDialog.onConfirm}
                            loading={saving}
                            variant="contained"
                            color={confirmDialog.type === 'warning' ? 'error' : 'primary'}
                            autoFocus
                        >
                            Confirm
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default PermissionManagement;
